import { useEffect, useState } from 'react';
import { subscribePwaState, promptInstall, isStandalone, isIos } from '@/pwa';
import { Button } from '@/components/ui/button';
import { Download, X, WifiOff, Apple } from 'lucide-react';

/**
 * Бейдж «Оффлайн» + одноразовый баннер «Установить приложение».
 *
 * - На Android/Chrome ловим событие beforeinstallprompt и показываем кнопку.
 * - На iOS события нет (Safari), поэтому показываем инструкцию «На экран Домой»
 *   при заходе с iPhone/iPad (если ещё не установлено).
 * - В режиме standalone (PWA уже установлена) баннер не показываем.
 * - «X» сохраняет dismiss в localStorage на 7 дней.
 */
const DISMISS_KEY = 'pwa-install-dismissed-at';
const DISMISS_DAYS = 7;

function wasRecentlyDismissed() {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 3600 * 1000;
  } catch { return false; }
}

export default function PwaStatusBanner() {
  const [state, setState] = useState({ canInstall: false, online: true });
  const [dismissed, setDismissed] = useState(false);
  const ios = isIos();
  const standalone = isStandalone();

  useEffect(() => subscribePwaState(setState), []);
  useEffect(() => { setDismissed(wasRecentlyDismissed()); }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) { /* ignore */ }
    setDismissed(true);
  };

  // 1) Offline-бейдж — самый приоритетный.
  if (!state.online) {
    return (
      <div className="fixed top-2 left-1/2 -translate-x-1/2 z-50 bg-rose-600 text-white px-3 py-1.5 rounded-sm shadow-lg flex items-center gap-2 text-xs font-semibold"
        data-testid="offline-banner">
        <WifiOff className="w-3.5 h-3.5" />
        Нет соединения — используется кешированная версия
      </div>
    );
  }

  if (standalone || dismissed) return null;

  // 2) Android/desktop Chrome — есть deferredPrompt
  if (state.canInstall) {
    return (
      <div className="fixed bottom-3 right-3 z-50 max-w-sm bg-white border border-blue-200 shadow-xl rounded-sm overflow-hidden"
        data-testid="pwa-install-banner">
        <div className="px-3 py-2.5 flex items-start gap-2.5">
          <div className="w-9 h-9 shrink-0 rounded-sm bg-[#0046AD] text-white grid place-items-center font-bold text-sm">BQ</div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-slate-800">Установить BaseQuote</div>
            <div className="text-[11px] text-slate-500 leading-relaxed">
              Запускайте площадку с главного экрана без браузера. Уведомления о торгах прямо на устройство.
            </div>
            <div className="mt-1.5 flex gap-1.5">
              <Button size="sm" onClick={async () => {
                const res = await promptInstall();
                if (res?.outcome !== 'accepted') dismiss();
              }}
                className="h-7 px-2.5 text-[11px] bg-[#0046AD] hover:bg-[#003A8C] rounded-sm"
                data-testid="pwa-install-btn">
                <Download className="w-3 h-3 mr-1" />
                Установить
              </Button>
              <Button size="sm" variant="ghost" onClick={dismiss}
                className="h-7 px-2 text-[11px] text-slate-500 hover:bg-slate-100"
                data-testid="pwa-install-later">
                Позже
              </Button>
            </div>
          </div>
          <button onClick={dismiss}
            className="text-slate-400 hover:text-slate-700"
            data-testid="pwa-install-close"
            aria-label="Закрыть">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  }

  // 3) iOS — beforeinstallprompt не существует. Показываем инструкцию.
  if (ios) {
    return (
      <div className="fixed bottom-3 left-3 right-3 sm:left-auto sm:right-3 sm:max-w-sm z-50 bg-white border border-blue-200 shadow-xl rounded-sm overflow-hidden"
        data-testid="pwa-ios-install-banner">
        <div className="px-3 py-2.5 flex items-start gap-2.5">
          <div className="w-9 h-9 shrink-0 rounded-sm bg-[#0046AD] text-white grid place-items-center">
            <Apple className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-slate-800">Установить BaseQuote на iPhone</div>
            <div className="text-[11px] text-slate-500 leading-relaxed mt-0.5">
              Нажмите кнопку «Поделиться» <span className="font-mono">⎋</span> в Safari → выберите <strong>«На экран „Домой“»</strong>.
            </div>
          </div>
          <button onClick={dismiss}
            className="text-slate-400 hover:text-slate-700"
            data-testid="pwa-ios-close"
            aria-label="Закрыть">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
